import { MathUtils, Vector3 } from "three";

const metersPerLat = 111_320;

function metersPerLon(lat: number) {
  return 111_320 * Math.cos(lat * MathUtils.DEG2RAD);
}

export type ElevMeta = {
  width: number;
  height: number;
  west: number;
  north: number;
  east: number;
  south: number;
  pixelWidth: number;
  pixelHeight: number;
  min: number;
  max: number;
  mean: number;
  originElev: number;
  attribution?: string;
};

export type ElevProfile = "quebec-cliff" | "flat" | "arenal-soft";

/** Mutable ENU origin — set via configureGeo before district load. */
let originLat = 46.81202;
let originLon = -71.20542;
let elevProfile: ElevProfile = "quebec-cliff";
let reliefExaggeration = 1.0;

let elevGrid: Float32Array | null = null;
let elevMeta: ElevMeta | null = null;

export function configureGeo(opts: {
  lat: number;
  lon: number;
  elevProfile?: ElevProfile;
  reliefExaggeration?: number;
}) {
  originLat = opts.lat;
  originLon = opts.lon;
  elevProfile = opts.elevProfile ?? "quebec-cliff";
  reliefExaggeration =
    opts.reliefExaggeration ??
    (elevProfile === "arenal-soft" || elevProfile === "flat" ? 1.35 : 1.0);
  elevGrid = null;
  elevMeta = null;
}

export function getGeoOrigin() {
  return { lat: originLat, lon: originLon };
}

/** Soft rolling foothills for La Fortuna — plaza flatter, land rises WNW toward Arenal. */
function arenalSoftElevation(lat: number, lon: number): number {
  const westM = -(lon - originLon) * metersPerLon(originLat);
  const northM = (lat - originLat) * metersPerLat;
  // Distance from church/plaza — keep the postcard frame readable
  const plazaR = Math.hypot(westM * 0.85, northM);
  const plazaFlatten = MathUtils.smoothstep(55, 140, plazaR);

  // Volcano lies WNW: blend west + slight north
  const towardVolcano = westM * 0.92 + northM * 0.35;
  const volcanoRamp = Math.max(0, towardVolcano) * 0.055; // ~22 m over 400 m

  // Gentle foothill undulation (deterministic, no noise lib)
  const roll =
    Math.sin(westM * 0.018 + northM * 0.011) * 2.8 +
    Math.sin(westM * 0.041 - northM * 0.027) * 1.6 +
    Math.cos(northM * 0.033 + westM * 0.009) * 1.2;

  // Soft valley along the main strip (Vía 142 / park block) so streets sit in the land
  const streetValley =
    -1.4 *
    Math.exp(-((northM + 25) ** 2) / (2 * 55 ** 2)) *
    Math.exp(-(westM ** 2) / (2 * 180 ** 2));

  const base = 4.5;
  const relief = (volcanoRamp + roll * plazaFlatten + streetValley) * plazaFlatten;
  // Near plaza keep a gentle dish, not a billiard table
  const plazaDish = (1 - plazaFlatten) * (plazaR * plazaR) * 0.00012;
  return base + relief + plazaDish;
}

/**
 * Approximate Cap Diamant cliff — used until / if DEM fails to load.
 * Flat / arenal-soft: foothill profiles without Québec cliff.
 */
function fallbackElevation(lat: number, lon: number): number {
  if (elevProfile === "arenal-soft" || elevProfile === "flat") {
    return arenalSoftElevation(lat, lon);
  }
  const cliffLon = -71.2039;
  const x = (cliffLon - lon) / 0.0018;
  const sigmoid = 1 / (1 + Math.exp(-MathUtils.clamp(x, -8, 8) * 4.2));
  const plateau = 52;
  const lower = 2.5;
  const northLift = Math.max(0, (lat - 46.8108) * 120);
  return lower + (plateau - lower) * sigmoid + northLift;
}

function sampleGrid(lat: number, lon: number): number | null {
  if (!elevGrid || !elevMeta) return null;
  const { width, height, west, north, pixelWidth, pixelHeight } = elevMeta;
  const col = (lon - west) / pixelWidth;
  const row = (lat - north) / pixelHeight;
  if (col < 0 || row < 0 || col >= width - 1 || row >= height - 1) return null;

  const c0 = Math.floor(col);
  const r0 = Math.floor(row);
  const fc = col - c0;
  const fr = row - r0;
  const at = (r: number, c: number) => elevGrid![r * width + c];
  return (
    (1 - fr) * ((1 - fc) * at(r0, c0) + fc * at(r0, c0 + 1)) +
    fr * ((1 - fc) * at(r0 + 1, c0) + fc * at(r0 + 1, c0 + 1))
  );
}

/**
 * Ground height in local ENU meters, **relative to the geo origin**.
 * Matches Google 3D Tiles ReorientationPlugin `{ height: 0 }` so twin and
 * photoreal share a vertical datum. Relief exaggeration scales deltas only.
 */
export function groundElevation(lat: number, lon: number): number {
  const raw = sampleGrid(lat, lon);
  if (raw == null) {
    const here = fallbackElevation(lat, lon);
    const atOrigin = fallbackElevation(originLat, originLon);
    return (here - atOrigin) * reliefExaggeration;
  }
  const origin = elevMeta?.originElev ?? raw;
  return (raw - origin) * reliefExaggeration;
}

export function elevationReady(): boolean {
  return elevGrid !== null;
}

export function getElevMeta(): ElevMeta | null {
  return elevMeta;
}

/** Load float32 DEM grid from district assets (MRNF MNT clip). */
export async function loadElevation(
  baseUrl = "/district/old-quebec",
): Promise<ElevMeta | null> {
  try {
    const [meta, buf] = await Promise.all([
      fetch(`${baseUrl}/elev.json`).then((r) => {
        if (!r.ok) throw new Error(`elev.json ${r.status}`);
        return r.json() as Promise<ElevMeta>;
      }),
      fetch(`${baseUrl}/elev.bin`).then(async (r) => {
        if (!r.ok) throw new Error(`elev.bin ${r.status}`);
        return r.arrayBuffer();
      }),
    ]);
    const expected = meta.width * meta.height;
    const grid = new Float32Array(buf);
    if (grid.length !== expected) {
      throw new Error(
        `elev.bin length ${grid.length} != ${expected} (${meta.width}x${meta.height})`,
      );
    }
    elevMeta = meta;
    elevGrid = grid;
    console.info(
      `[PlaceStage] elevation DEM loaded (${meta.width}×${meta.height}, ${meta.min.toFixed(0)}–${meta.max.toFixed(0)} m)`,
    );
    return meta;
  } catch (err) {
    console.warn(
      elevProfile === "arenal-soft" || elevProfile === "flat"
        ? "[PlaceStage] DEM unavailable, using La Fortuna soft foothills"
        : "[PlaceStage] DEM unavailable, using procedural cliff",
      err,
    );
    elevMeta = null;
    elevGrid = null;
    return null;
  }
}

/** Local ENU: X west, Y up, Z north. `height` is meters above local ground. */
export function latLonToLocal(lat: number, lon: number, height = 0): Vector3 {
  const dLat = lat - originLat;
  const dLon = lon - originLon;
  const elev = groundElevation(lat, lon);
  return new Vector3(
    -(dLon * metersPerLon(originLat)),
    elev + height,
    dLat * metersPerLat,
  );
}

export function lonLatToLocal(lon: number, lat: number, height = 0): Vector3 {
  return latLonToLocal(lat, lon, height);
}

export function ringCentroid(ring: number[][]): { lon: number; lat: number } {
  let lon = 0;
  let lat = 0;
  const n = Math.max(1, ring.length - 1);
  for (let i = 0; i < n; i++) {
    lon += ring[i][0];
    lat += ring[i][1];
  }
  return { lon: lon / n, lat: lat / n };
}
